import React from 'react';
import { FiUsers, FiUser, FiTrendingUp, FiAward, FiTarget, FiSettings, FiUserCheck, FiBriefcase, FiZap, FiFileText } from 'react-icons/fi';

const StakeholdersTab = ({ insightData, Section }) => {
  if (!insightData?.heptapod_dimensional_analysis?.stakeholder_intelligence) return null;

  const stakeholderIntel = insightData.heptapod_dimensional_analysis.stakeholder_intelligence;

  return (
    <>
      {/* Stakeholder Intelligence */}
      <Section title="Stakeholder Intelligence" iconComponent={FiUsers}>
        <div className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 mb-6">
          {(() => {
            // Group stakeholders by role_type
            const roleGroups = stakeholderIntel.individual_profiles.reduce((acc, profile) => {
              const roleType = profile.role_type || 'Other Stakeholder';
              if (!acc[roleType]) {
                acc[roleType] = [];
              }
              acc[roleType].push(profile);
              return acc;
            }, {});

            // Define the preferred order for role types (with multiple possible formats)
            const roleTypeOrder = [
              ['Decision Maker', 'decision_maker', 'decisionmaker', 'decision maker'],
              ['Champion', 'champion'],
              ['Technical', 'technical'],
              ['Economic Buyer', 'economic_buyer', 'economicbuyer', 'economic buyer'],
              ['User', 'user'],
              ['Influencer', 'influencer']
            ];

            // Function to get display name for role type
            const getDisplayName = (roleType) => {
              const roleMapping = {
                'decision_maker': 'Decision Maker',
                'decisionmaker': 'Decision Maker',
                'decision maker': 'Decision Maker',
                'champion': 'Champion',
                'technical': 'Technical',
                'economic_buyer': 'Economic Buyer',
                'economicbuyer': 'Economic Buyer',
                'economic buyer': 'Economic Buyer',
                'user': 'User',
                'influencer': 'Influencer'
              };
              return roleMapping[roleType] || roleType;
            };

            // Function to get icon for role type
            const getRoleIcon = (roleType) => {
              if (roleType === 'Decision Maker' || roleType === 'decision_maker' || roleType === 'decisionmaker' || roleType === 'decision maker') {
                return <FiAward className="w-4 h-4" />;
              }
              if (roleType === 'Influencer' || roleType === 'influencer') {
                return <FiTarget className="w-4 h-4" />;
              }
              if (roleType === 'Technical' || roleType === 'technical') {
                return <FiSettings className="w-4 h-4" />;
              }
              if (roleType === 'User' || roleType === 'user') {
                return <FiUserCheck className="w-4 h-4" />;
              }
              if (roleType === 'Economic Buyer' || roleType === 'economic_buyer' || roleType === 'economicbuyer' || roleType === 'economic buyer') {
                return <FiBriefcase className="w-4 h-4" />;
              }
              if (roleType === 'Champion' || roleType === 'champion') {
                return <FiZap className="w-4 h-4" />;
              }
              return <FiFileText className="w-4 h-4" />;
            };

            // Sort role groups according to preferred order
            const sortedRoleGroups = {};
            roleTypeOrder.forEach((roleVariants, index) => {
              // Find the first matching role type from the variants
              const matchingRoleType = roleVariants.find(variant => roleGroups[variant]);
              if (matchingRoleType) {
                sortedRoleGroups[matchingRoleType] = roleGroups[matchingRoleType];
              }
            });

            // Add any remaining role types that weren't in the preferred order
            Object.keys(roleGroups).forEach(roleType => {
              const isInPreferredOrder = roleTypeOrder.some(variants => 
                variants.includes(roleType)
              );
              if (!isInPreferredOrder) {
                sortedRoleGroups[roleType] = roleGroups[roleType];
              }
            });

            const roleTypeColors = {
              'Decision Maker': '#0891b2',
              'decision_maker': '#0891b2',
              'decisionmaker': '#0891b2',
              'decision maker': '#0891b2',
              'Influencer': '#0891b2',
              'influencer': '#0891b2',
              'Technical': '#0891b2', 
              'technical': '#0891b2',
              'User': '#0891b2',
              'user': '#0891b2',
              'Economic Buyer': '#0891b2',
              'economic_buyer': '#0891b2',
              'economicbuyer': '#0891b2',
              'economic buyer': '#0891b2',
              'Champion': '#0891b2',
              'champion': '#0891b2',
              'Other Stakeholder': '#6b7280'
            };

            return (
              <div className="relative py-6">
                {/* Organization Header */}
                <div className="text-center mb-10 relative">
                  <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white px-6 py-4 rounded-xl text-base font-bold inline-block shadow-lg flex items-center gap-2">
                    <FiTrendingUp className="w-5 h-5" />
                    ICP Mapping
                  </div>
                  
                  {/* Vertical trunk line */}  
                  {/* <div style={{
                    position: 'absolute',
                    left: '50%',
                    top: '100%',
                    width: 3,
                    height: 30,
                    background: 'linear-gradient(to bottom, #667eea, #94a3b8)',
                    transform: 'translateX(-50%)',
                    borderRadius: '0 0 2px 2px'
                  }}></div> */}
                </div>

                {/* Role Type Branches */}
                <div className="flex flex-col gap-8 relative items-center">
                  {Object.entries(sortedRoleGroups).map(([roleType, profiles], roleIdx) => (
                    <div key={roleType} className="w-full">
                      {/* Role Type Header */}
                      <div className="flex justify-center mb-6">
                        <div className="bg-gray-800 text-white px-5 py-3 rounded-xl text-sm font-semibold shadow-lg relative flex items-center gap-2 z-10">
                          {getRoleIcon(roleType)}
                          {getDisplayName(roleType)} ({profiles.length})
                        </div>
                      </div>

                      {/* Stakeholders Grid */}
                      <div className="flex justify-center">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-4xl w-full">
                          {profiles.map((profile, profileIdx) => (
                            <div key={profileIdx} className="border-2 border-gray-200 rounded-xl p-4 bg-white shadow-lg transition-all duration-200 cursor-pointer relative hover:shadow-xl hover:-translate-y-1">
                              {/* Profile Header */}
                              <div className="flex items-center gap-3 mb-4">
                                <div className="bg-gradient-to-br from-gray-800 to-gray-900 text-white rounded-full w-11 h-11 flex items-center justify-center text-base font-bold shadow-lg">
                                  {profile.name?.charAt(0) || 'S'}
                                </div>
                                <div className="flex-1">
                                  <div className="font-bold text-gray-900 text-sm mb-1">
                                    {profile.name}
                                  </div>
                                  <div className="text-xs text-gray-600 font-semibold">
                                    {profile.influence_level} influence
                                  </div>
                                </div>
                              </div>
                              
                              {/* Engagement Status */}
                              <div className="mb-4">
                                <div className="text-xs text-gray-500 mb-2 font-semibold">
                                  ENGAGEMENT STATUS
                                </div>
                                <span className={`px-3 py-1 rounded-lg text-xs font-bold uppercase tracking-wide ${
                                  profile.engagement_status === 'active' 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {profile.engagement_status}
                                </span>
                              </div>

                              {/* Engagement Strategy */}
                              {profile.engagement_strategy && (
                                <div className="text-xs text-gray-700 leading-relaxed p-3 bg-gray-50 rounded-lg border border-gray-200">
                                  <div className="font-semibold mb-1 text-gray-500 text-xs">
                                    ENGAGEMENT STRATEGY
                                  </div>
                                  {profile.engagement_strategy}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Central connecting line for role branches - removed */}
              </div>
            );
          })()}
        </div>
      </Section>

      {/* Group Dynamics */}
      {stakeholderIntel.group_dynamics && (
        <Section title="Group Dynamics" iconComponent={FiUsers}>
          <div className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 mb-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Power Structure */}
              {stakeholderIntel.group_dynamics.power_structure && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-4">
                    Power Structure
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <div className="text-xs text-gray-500 mb-2">Formal Authority</div>
                      <div className="flex flex-wrap gap-2">
                        {stakeholderIntel.group_dynamics.power_structure.formal_authority?.map((role, idx) => (
                          <span key={idx} className="bg-blue-50 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                            {role}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-2">Informal Influence</div>
                      <div className="flex flex-wrap gap-2">
                        {stakeholderIntel.group_dynamics.power_structure.informal_influence?.map((role, idx) => (
                          <span key={idx} className="bg-green-50 text-green-800 px-2 py-1 rounded text-xs font-medium">
                            {role}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Coalition Patterns */}
              {stakeholderIntel.group_dynamics.coalition_patterns && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-4">
                    Coalition Patterns
                  </h4>
                  <div className="space-y-3">
                    {stakeholderIntel.group_dynamics.coalition_patterns.alliances?.map((alliance, idx) => (
                      <div key={idx} className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <div className="text-xs font-semibold text-green-800 mb-1">
                          Alliance ({alliance.strength} strength)
                        </div>
                        <div className="text-xs text-gray-700">
                          {alliance.members.join(', ')}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Section>
      )}
    </>
  );
};

export default StakeholdersTab;
